import fetchJsonFromRpgLogsApi from './fetchJsonFromRpgLogsApi';
import { GameViewModel } from '../../resources/js/typings/viewModels/gameViewModel';
import { BuildsZonePageSlugsViewModel } from './getBuildsZonePageSlugs';
import { SelectListItem } from '../../resources/js/typings/viewModels/selectListItemViewModel';
import SeoViewModel from '../../resources/js/typings/viewModels/seoViewModel';
import getCommonViewModels, { CommonViewModels } from './getCommonViewModels';
import { TableColumn } from '../../resources/js/components/Table/Table';
import { BreadcrumbViewModel } from '../components/ArchonHeader/ArchonHeader';
import { BuildsSubcreationMigrationViewModel } from '@resources/js/components/BuildsSubcreationMigrationSection/BuildsSubcreationMigrationSection';
import { BuildsAdvancedSettingsViewModel } from '@resources/js/components/BuildsAdvancedSettings/BuildsAdvancedSettings';

export type BuildsZonePageViewModel = {
  seo: SeoViewModel;
  breadcrumbs: BreadcrumbViewModel[];
  title: string;
  description: string;
  lastUpdatedLabel: string;
  lastUpdated: string | null;
  totalParsesLabel: string;
  totalParses: number;
  roleLabel: string;
  roleOptions: SelectListItem<string>[];
  selectedRole: string;
  zoneTypeLabel: string;
  zoneBackgroundUrl: string;
  zoneTypeOptions: SelectListItem<string>[];
  selectedZoneType: string;
  showDifficultySelect: boolean;
  difficultyLabel: string;
  difficultyOptions: SelectListItem<string>[];
  selectedDifficulty: string;
  encounterLabel: string;
  encounterOptions: SelectListItem<string>[];
  selectedEncounter: string;
  affixesLabel: string;
  affixesOptions: SelectListItem<string>[];
  selectedAffixes: string;
  specTierListSection: BuildsTierListSectionViewModel;
  specRankingsSection: BuildsRankingsSectionViewModel;
  heroSpecRankingsSection: BuildsRankingsSectionViewModel;
  encounterTierListSection: BuildsTierListSectionViewModel | null;
  encounterRankingsSection: BuildsRankingsSectionViewModel | null;
  advancedSettings: BuildsAdvancedSettingsViewModel;
  metadataSummaryDescription: string;
  warning: string | null;
  archonTitleBadgeLabel?: string;
  archonTitleBadgeTooltip?: string;
  subcreationMigrationSection?: BuildsSubcreationMigrationViewModel;
};

export type BuildsTierListSectionViewModel = {
  title: string;
  description: string;
  label: string | null;
  metrics: (SelectListItem<string> & {
    description: string;
  })[];
  tierLists: BuildsTierList[];
};

export type BuildsTierList = {
  metric: string;
  tiers: Array<{
    tier: string;
    entries: BuildsTierListEntry[][];
  }>;
  specValueAndLabelPadding?: string | null;
};

export type BuildsTierListEntry = {
  id: number;
  name: string;
  url: string;
  cssClassName: string;
  icon: string;
  value: string | null;
  label: string | null;
  iconType: 'actor' | 'encounter';
  tierListGroupLabel: string | null;
};

export type BuildsRankingsSectionTableAccessor =
  | 'item'
  | 'maxKey'
  | 'minKey'
  | 'dps'
  | 'rdps'
  | 'adps'
  | 'cdps'
  | 'hps'
  | 'rhps'
  | 'score'
  | 'survivability'
  | 'popularity'
  | 'parses'
  | 'bar'
  | 'usage';

export type BuildsRankingsSectionTableRow = {
  item: string;
  itemPath: string;
  maxKey?: number;
  minKey?: number;
  dps?: number;
  rdps?: number;
  adps?: number;
  cdps?: number;
  hps?: number;
  rhps?: number;
  score?: number;
  survivability?: number;
  popularity: number;
  parses: number;
  bar?: string;
  usage?: number;
};

export type BuildsRankingsSectionViewModel = {
  title: string;
  description: string;
  table: {
    columns: Record<
      BuildsRankingsSectionTableAccessor,
      TableColumn | null | undefined
    >;
    data: BuildsRankingsSectionTableRow[];
  };
};

async function getBuildsZonePageViewModels(
  slugs: BuildsZonePageSlugsViewModel,
  translationKeys: string[]
): Promise<
  CommonViewModels & {
    game: GameViewModel;
    translations: Record<string, string>;
    buildsZonePage: BuildsZonePageViewModel | null;
  }
> {
  const data = await fetchJsonFromRpgLogsApi(
    `query($gameSlug: String!, $rankingsSlug: String!, $zoneTypeSlug: String!, $difficultySlug: String!, $encounterSlug: String!, $affixesSlug: String, $translationKeys: [String!]!) {
      viewModels {
        game
        buildsZonePage(
          gameSlug: $gameSlug,
          rankingsSlug: $rankingsSlug,
          zoneTypeSlug: $zoneTypeSlug,
          difficultySlug: $difficultySlug,
          encounterSlug: $encounterSlug,
          affixesSlug: $affixesSlug
        )
        translations(keys: $translationKeys)
      }
    }`,
    { ...slugs, translationKeys },
    slugs.gameSlug
  );

  return {
    ...(await getCommonViewModels(slugs.gameSlug)),
    ...data.viewModels,
  };
}

export default getBuildsZonePageViewModels;
